import { defineNuxtPlugin } from 'nuxt/app'
import { useTaskPolling } from '@/composables/useTaskPolling'

/**
 * This plugin controls when task polling should occur based on:
 * - Tab visibility (document.hidden)
 * - User interaction (meaningful clicks only)
 *
 * Polling is paused when the tab is not visible to reduce server load.
 */
export default defineNuxtPlugin({
  name: 'polling-gate',
  setup() {
    // Skip in test environment or SSR
    if (typeof window === 'undefined' || process.env.NODE_ENV === 'test') {
      return
    }

    // Initialize the composable in the proper Nuxt context (NOW, not in setTimeout)
    const { resumePolling, pausePolling, triggerImmediatePoll } = useTaskPolling()

    let inactivityTimer: number | null = null
    const INACTIVITY_TIMEOUT = 30_000 // Stop polling after 30s of no user activity
    const INTERACTION_POLL_THROTTLE = 5000 // Only poll once per 5 seconds from user interaction
    let lastInteractionPoll = 0

    // Reset inactivity timer - stops polling after 30s of no activity
    const resetInactivityTimer = () => {
      if (inactivityTimer !== null) {
        clearTimeout(inactivityTimer)
      }

      inactivityTimer = window.setTimeout(() => {
        pausePolling()
      }, INACTIVITY_TIMEOUT)
    }

    // Handle tab visibility changes (declared at plugin scope for proper cleanup)
    const handleVisibilityChange = () => {
      if (document.hidden) {
        if (inactivityTimer !== null) {
          clearTimeout(inactivityTimer)
          inactivityTimer = null
        }
        pausePolling()
      }
      else {
        resumePolling()
        resetInactivityTimer()
      }
    }

    // Handle user clicks - reset inactivity timer on ANY click (declared at plugin scope)
    const handleClick = (event: MouseEvent) => {
      if (document.hidden) return

      // Always resume polling on any click (it's a no-op if already polling)
      resumePolling()

      // Reset inactivity timer on ANY click
      resetInactivityTimer()

      // Optionally trigger immediate poll for interactive elements (throttled)
      const target = event.target as HTMLElement
      const isInteractiveElement
        = target.tagName === 'BUTTON'
          || target.tagName === 'A'
          || target.closest('button') !== null
          || target.closest('a') !== null
          || target.getAttribute('role') === 'button'
          || target.getAttribute('role') === 'link'

      if (isInteractiveElement) {
        const now = Date.now()
        if (now - lastInteractionPoll >= INTERACTION_POLL_THROTTLE) {
          lastInteractionPoll = now
          triggerImmediatePoll()
        }
      }
    }

    // Wait a tick for other plugins to initialize (like auth), then start polling
    const initTimer = setTimeout(() => {
      // Double-check document exists (important for test environments)
      if (typeof document === 'undefined') {
        return
      }

      // Set up event listeners
      document.addEventListener('visibilitychange', handleVisibilityChange)
      document.addEventListener('click', handleClick)

      // Start polling if tab is visible
      if (!document.hidden) {
        resumePolling()
        resetInactivityTimer()
      }
    }, 100) // Small delay to let auth load

    // Return cleanup function to prevent timers running after teardown
    return {
      onUnmount() {
        clearTimeout(initTimer)
        if (inactivityTimer !== null) {
          clearTimeout(inactivityTimer)
        }
        if (typeof document !== 'undefined') {
          document.removeEventListener('visibilitychange', handleVisibilityChange)
          document.removeEventListener('click', handleClick)
        }
      },
    }

    // Note: Event listeners persist for the lifetime of the SPA
    // When the page unloads, the entire browser context (including listeners) is destroyed
  },
})
